// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import VariablesEdit from './Edit';

import { variable, VariableDO } from '../../requests';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: VariableDO[];
}

export default class List extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM']),
      del: hasLevel('role', ['ADM']),
      edit: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    this.loadingStart();
    variable<VariableDO[]>('list')
      ?.then((resp: VariableDO[]) => {
        this.setLocalState({ data: resp });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'VE';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Variable Edit',
      component: VariablesEdit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'VARIABLE',
        title: 'Variable',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (item: VariableDO): void => {
    window.store.dispatch(removeTab('VE'));
    const rowId: number = item?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: VariableDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.name}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        variable('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load();
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      name: 'Name',
      unit: 'Unit',
      value: 'Code',
      graph: 'Plottable',
      alarm: 'Alarm Allowed'
    };
    const columStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      unit: { textAlign: 'left' },
      value: { textAlign: 'left' },
      graph: { textAlign: 'center' },
      alarm: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          customCellStyles={columStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
